/** Admin-facing copy for the metered usage facts and finalize-actuals flow. */

export const METERED_FACTS_TITLE = 'Metered usage';
export const FACT_LABEL_UNIT = 'Unit';
export const FACT_LABEL_ESTIMATED = 'Estimated';
export const FACT_LABEL_ACTUAL = 'Actual';
export const FACT_LABEL_CAP = 'Charge cap';
export const FACT_LABEL_PAID = 'Paid so far';
export const FACT_LABEL_DUE = 'Amount due';
export const FACT_LABEL_FINALIZED = 'Actuals finalized';
export const FACT_LABEL_RESOLVED = 'Uncollected resolved';
export const FACT_ACTUAL_NOT_RECORDED = 'Not recorded';
export const FACT_EMPTY_VALUE = '—';

export const FINALIZE_TITLE = 'Finalize actuals';
export const FINALIZE_CLOSE_LABEL = 'Close';
export const FINALIZE_ESTIMATE_LABEL = 'Estimate';
export const FINALIZE_CAP_LABEL = 'Charge cap';
export const FINALIZE_NOTE_LABEL = 'Evidence note';
export const FINALIZE_NOTE_PLACEHOLDER = 'How was the actual usage verified?';
export const FINALIZE_SUBMIT_LABEL = 'Review and finalize';
export const FINALIZE_CONFIRM_LABEL = 'Finalize';
export const FINALIZE_CONFIRM_TITLE = 'Finalize actuals?';
export const FINALIZE_SUBMITTING_LABEL = 'Finalizing…';

export const finalizeUnitLabel = (unitPlural: string): string => `Actual ${unitPlural} delivered`;

export const finalizeConfirmBody = (deliveredText: string): string =>
  `This records ${deliveredText} delivered and may charge the guest's saved card now.`;

export const FINALIZE_OUTCOME_COMPLETED =
  'Actuals finalized. Usage was within the estimate, so nothing more was charged and the booking is completed.';
export const FINALIZE_OUTCOME_DELTA_CHARGED =
  'Actuals finalized. The balance was charged to the guest’s saved card.';
export const FINALIZE_OUTCOME_OVER_CAP =
  'Actuals finalized. The total went over the charge cap: the cap amount was charged and the guest has been asked to approve the excess.';
export const FINALIZE_OUTCOME_NOT_COLLECTED =
  'Actuals finalized, but the balance could not be collected. The amount due remains and the guest gets reminder emails.';
export const FINALIZE_OUTCOME_NOT_COLLECTED_EXCESS_SUFFIX =
  ' The guest has been asked to pay the full balance, including the amount over the charge cap, from their booking page.';
export const FINALIZE_OUTCOME_PENDING =
  'Actuals finalized. The charge result is not confirmed yet; the booking updates when the payment provider reports back.';

export const FINALIZE_ERROR_FALLBACK = 'Failed to finalize actuals. Please try again.';

export const FINALIZE_ERROR_CODE_MESSAGES: Record<string, string> = {
  EVIDENCE_NOTE_BLANK: 'Add a note describing how the actual usage was verified.',
  ACTUALS_IMMUTABLE_AFTER_CHARGE:
    'Actuals can no longer be changed because a balance charge has gone through or is in progress. The booking has been refreshed.',
  FINALIZE_REQUIRES_METERED: "This booking isn't billed by usage.",
  FINALIZE_SNAPSHOT_MISSING:
    "This booking has no booking-time rate on record, so actuals can't be finalized. Escalate to engineering.",
};

export const RESOLVE_TITLE = 'Resolve unpaid balance';
export const RESOLVE_CLOSE_LABEL = 'Close';
export const RESOLVE_NOTE_LABEL = 'Resolution note';
export const RESOLVE_NOTE_PLACEHOLDER = 'Why is this balance being resolved without collection?';
export const RESOLVE_SUBMIT_LABEL = 'Review and resolve';
export const RESOLVE_SUBMITTING_LABEL = 'Resolving…';
export const RESOLVE_CONFIRM_LABEL = 'Resolve';
export const RESOLVE_CONFIRM_TITLE = 'Resolve unpaid balance?';
export const RESOLVE_CONFIRM_CLOSING =
  'Resolving completes the booking; no further charge will be attempted.';

export const resolveOutstandingNow = (amountText: string): string =>
  `Outstanding now: ${amountText}`;

export const resolveAwaitingApproval = (amountText: string): string =>
  `Above the charge cap, awaiting guest approval: ${amountText}`;

export const resolveOutcomeSettledAtCap = (finalText: string): string =>
  `Settled at the charge cap: amount in force lowered to ${finalText}; booking completed.`;

export const resolveOutcomeWrittenOff = (shortfallText: string): string =>
  `Written off: ${shortfallText} not collected; booking completed.`;

export const RESOLVE_ERROR_FALLBACK = 'Failed to resolve the unpaid balance. Please try again.';

export const RESOLVE_ERROR_CODE_MESSAGES: Record<string, string> = {
  RESOLUTION_NOTE_BLANK: 'Add a note explaining why this balance is being resolved.',
  RESOLVE_REQUIRES_METERED: "This booking isn't billed by usage.",
  RESOLVE_REQUIRES_DELIVERED_CONFIRMED:
    'This booking is no longer awaiting a balance. The booking has been refreshed.',
  RESOLVE_CHARGE_IN_FLIGHT:
    'A charge for this booking is in progress. Wait for it to finish, then try again.',
  RESOLVE_GUEST_PAYMENT_IN_PROGRESS:
    'The guest is paying this balance now. Wait a few minutes, then refresh.',
  RESOLVE_NOTHING_OUTSTANDING:
    'Nothing is outstanding on this booking. The booking has been refreshed.',
};

export const NEEDS_ACTUALS_LABEL = 'Needs actuals';
export const METERED_FILTER_LABEL = 'Metered usage';
export const METERED_FILTER_ARIA_LABEL = 'Filter bookings by metered usage';
export const METERED_FILTER_ALL = 'All';

// ─── Billed by usage: option row authoring ──────────────────────────────────

export const BILLED_BY_USAGE_LABEL = 'Billed by usage';

export const billedByUsageHelper = (capMultiplier: number): string =>
  `The guest books an estimated amount of time and is charged that estimate when the booking is confirmed. After the service you record the actual time used. Extra time is charged at the same rate, automatically up to ${capMultiplier}× the estimate and beyond that only with the guest's approval. Using less than the estimate doesn't reduce the charge. Existing bookings keep their original terms.`;

export const BILLED_BY_USAGE_REASONS = {
  by_request: 'Not available for options priced by request.',
  unit: 'Only available for options priced per hour, per 30 min or per day.',
  rate: 'Set a price for this option first.',
} as const;

export const BILLED_BY_USAGE_CLEARED_NOTES = {
  unit: 'Billed by usage was turned off because this option is no longer priced per hour, per 30 min or per day.',
  by_request: 'Billed by usage was turned off because this option is now priced by request.',
} as const;

export const unitBoundLabel = (boundary: 'Minimum' | 'Maximum', unitNoun: string): string =>
  `${boundary} ${unitNoun}`;

export const UNIT_BOUND_PLACEHOLDER = 'optional';

export const UNIT_BOUNDS_ERROR =
  "Minimum and maximum must each be at least 1, and the maximum can't be below the minimum.";

/** Appended after a price to mark a metered service or option. */
export const BY_USAGE_SUFFIX = ' · by usage';
